/**
 * YOLO11 Pose Detector using ONNX Runtime Web (WebGPU backend)
 *
 * Loads a YOLO11 pose model in ONNX format and detects human poses
 * with 17 COCO keypoints.
 *
 * Output format is compatible with MoveNet (@tensorflow-models/pose-detection)
 * so it can be used as a drop-in replacement.
 *
 * Model output shape: (1, 56, 8400) where:
 *   - 56 = 4 (bbox x,y,w,h) + 1 (confidence) + 51 (17 keypoints * 3)
 *   - 8400 = number of candidate detection boxes
 */

import * as ort from 'onnxruntime-web/all';

// Same-origin ORT wasm — see onnxDetector.js note.
ort.env.wasm.wasmPaths = '/ort/';

if (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) {
  ort.env.wasm.numThreads = Math.min(navigator.hardwareConcurrency, 8);
}
if (ort.env.webgpu) {
  ort.env.webgpu.powerPreference = 'high-performance';
}

const DEFAULT_INPUT_SIZE = 320;
const DEFAULT_CONFIDENCE_THRESHOLD = 0.25;
const DEFAULT_IOU_THRESHOLD = 0.45;

// COCO keypoint names (17 keypoints)
const KEYPOINT_NAMES = [
  'nose',
  'left_eye',
  'right_eye',
  'left_ear',
  'right_ear',
  'left_shoulder',
  'right_shoulder',
  'left_elbow',
  'right_elbow',
  'left_wrist',
  'right_wrist',
  'left_hip',
  'right_hip',
  'left_knee',
  'right_knee',
  'left_ankle',
  'right_ankle'
];

/**
 * ONNX YOLO11 Pose Detector
 *
 * Provides MoveNet-compatible pose detection using YOLO11 pose model with WebGPU.
 */
export class OnnxYoloPoseDetector {
  constructor(modelPath, options = {}) {
    this.modelPath = modelPath;
    this.inputSize = options.inputSize || DEFAULT_INPUT_SIZE;
    this.confThreshold = options.confThreshold || DEFAULT_CONFIDENCE_THRESHOLD;
    this.iouThreshold = options.iouThreshold || DEFAULT_IOU_THRESHOLD;

    this.session = null;
    this.isInitialized = false;

    this.modelInfo = {
      modelName: null,
      deployedAt: null
    };

    this._lastScale = null;

    this._canvas = null;
    this._ctx = null;

    this._frameCount = 0;
  }

  async initialize(timeout = 30000) {
    if (this.isInitialized) return true;

    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`Model initialization timed out after ${timeout}ms`)), timeout);
    });

    await Promise.race([
      this._doInitialize(),
      timeoutPromise
    ]);
    return true;
  }

  async _doInitialize() {
    const modelDir = this.modelPath.replace(/\/[^/]+$/, '');
    let actualModelUrl = this.modelPath;

    try {
      let manifest = null;
      const unifiedUrl = `${modelDir}/manifest.detector.json?_=${Date.now()}`;
      const unifiedResp = await fetch(unifiedUrl, { cache: 'no-store' });
      if (unifiedResp.ok) {
        manifest = await unifiedResp.json();
      } else {
        const onnxUrl = `${modelDir}/manifest.onnx.pose.json?_=${Date.now()}`;
        const onnxResp = await fetch(onnxUrl, { cache: 'no-store' });
        if (onnxResp.ok) {
          manifest = await onnxResp.json();
        }
      }

      if (manifest && manifest.onnxModel) {
        actualModelUrl = `${modelDir}/${manifest.onnxModel}`;
        this.modelInfo = {
          modelName: manifest.modelName || null,
          deployedAt: manifest.deployedAt || null
        };
        console.log(`[OnnxPose] Model: ${manifest.modelName || 'unknown'}, deployed: ${manifest.deployedAt || 'unknown'}`);
        if (manifest.inputSize) {
          this.inputSize = manifest.inputSize;
        }
      }
    } catch (_e) {
      // Use default path
    }

    console.log('[OnnxPose] Loading model from:', actualModelUrl);

    this._modelUrl = actualModelUrl;

    await this._createSession();

    this._canvas = document.createElement('canvas');
    this._canvas.width = this.inputSize;
    this._canvas.height = this.inputSize;
    // No willReadFrequently — we hand off via createImageBitmap, not getImageData.
    this._ctx = this._canvas.getContext('2d');

    this.isInitialized = true;
    return true;
  }

  async _createSession() {
    const params = typeof location !== 'undefined'
      ? new URLSearchParams(location.search) : new URLSearchParams();
    const debugPartition = params.get('partition') === '1';
    // WebGPU works again now that pose model was downgraded to opset 20 (was 22).
    // See onnxDetector.js for the ORT opset-22 Conv-registration gap.
    const backend = params.get('pose_backend') || 'webgpu';
    const layout = params.get('layout') || 'NHWC';
    const validation = params.get('validation') || 'wgpuOnly';

    let eps;
    if (backend === 'wasm') {
      eps = ['wasm'];
    } else if (backend === 'mixed') {
      eps = [{ name: 'webgpu', preferredLayout: layout, validationMode: validation }, 'wasm'];
    } else {
      eps = [{ name: 'webgpu', preferredLayout: layout, validationMode: validation }];
    }

    console.log('[OnnxPose] Loading with backend:', backend, '(EPs:', eps.map(e => typeof e === 'string' ? e : e.name).join(','), ')');
    try {
      this.session = await ort.InferenceSession.create(this._modelUrl, {
        executionProviders: eps,
        graphOptimizationLevel: 'all',
        enableCpuMemArena: true,
        enableMemPattern: true,
        ...(debugPartition ? { logSeverityLevel: 0, logVerbosityLevel: 1 } : {})
      });
      console.log('[OnnxPose] Model loaded successfully');
    } catch (e) {
      throw new Error(`ONNX session create failed with ${backend}. Error: ${e.message}`);
    }
  }

  /**
   * Preprocess image for YOLO inference.
   * Returns an ort.Tensor produced by the native fromImage path — the
   * uint8->float32 conversion and NCHW transpose run in ONNX Runtime,
   * not a JS pixel loop.
   *
   * @param {HTMLVideoElement|HTMLImageElement|HTMLCanvasElement} source
   * @returns {Promise<ort.Tensor>} float32 tensor shaped [1, 3, H, W]
   */
  async preprocess(source) {
    const srcWidth = source.videoWidth || source.width;
    const srcHeight = source.videoHeight || source.height;

    const scale = Math.min(this.inputSize / srcWidth, this.inputSize / srcHeight);
    const newWidth = Math.round(srcWidth * scale);
    const newHeight = Math.round(srcHeight * scale);
    const offsetX = Math.floor((this.inputSize - newWidth) / 2);
    const offsetY = Math.floor((this.inputSize - newHeight) / 2);

    this._lastScale = { scale, offsetX, offsetY, srcWidth, srcHeight };

    this._ctx.fillStyle = 'rgb(128,128,128)';
    this._ctx.fillRect(0, 0, this.inputSize, this.inputSize);
    this._ctx.drawImage(source, offsetX, offsetY, newWidth, newHeight);

    const bitmap = await createImageBitmap(this._canvas);
    try {
      return await ort.Tensor.fromImage(bitmap, {
        tensorLayout: 'NCHW',
        tensorFormat: 'RGB',
        dataType: 'float32'
      });
    } finally {
      bitmap.close();
    }
  }

  /**
   * Estimate poses from an image/video element.
   * Returns array of poses compatible with MoveNet format.
   *
   * @param {HTMLVideoElement|HTMLImageElement|HTMLCanvasElement} source
   * @returns {Array} Array of poses: [{keypoints: [{x, y, score, name}, ...], score}]
   */
  async estimatePoses(source) {
    if (!this.isInitialized) {
      throw new Error('[OnnxPose] Detector not initialized. Call initialize() first.');
    }

    let inputTensor = null;
    let results = null;

    try {
      inputTensor = await this.preprocess(source);

      results = await this.session.run({ images: inputTensor });

      const output = results.output || results[Object.keys(results)[0]];
      const outputData = output.data;
      const shape = output.dims;

      return this.postprocess(outputData, shape);
    } catch (error) {
      console.error('[OnnxPose] Detection error:', error);
      return [];
    } finally {
      if (inputTensor) {
        inputTensor.dispose();
      }
      if (results) {
        for (const key of Object.keys(results)) {
          if (results[key] && typeof results[key].dispose === 'function') {
            results[key].dispose();
          }
        }
      }
    }
  }

  /**
   * Postprocess YOLO pose output to get poses with keypoints
   *
   * Output format: (1, 56, N) where:
   *   - 56 = 4 (bbox x,y,w,h) + 1 (confidence) + 51 (17 keypoints * 3)
   *   - N = number of detection boxes (e.g., 2100 for 320x320 input)
   */
  postprocess(data, shape) {
    const numFeatures = shape[1];
    const numBoxes = shape[2];
    const numKeypoints = 17;

    if (!this._loggedFormat) {
      console.log(`[OnnxPose] Output shape: (${shape.join(', ')}), features=${numFeatures}, boxes=${numBoxes}`);

      const confs = [];
      for (let i = 0; i < Math.min(100, numBoxes); i++) {
        confs.push(data[4 * numBoxes + i]);
      }
      confs.sort((a, b) => b - a);
      console.log(`[OnnxPose] Top 10 confidences: ${confs.slice(0, 10).map(c => c.toFixed(4)).join(', ')}`);
      this._loggedFormat = true;
    }

    const { scale, offsetX, offsetY, srcWidth, srcHeight } = this._lastScale;

    const detections = [];

    for (let i = 0; i < numBoxes; i++) {
      const conf = data[4 * numBoxes + i];

      if (conf < this.confThreshold) continue;

      const cx = data[0 * numBoxes + i];
      const cy = data[1 * numBoxes + i];
      const w = data[2 * numBoxes + i];
      const h = data[3 * numBoxes + i];

      const x1 = (cx - w / 2 - offsetX) / scale;
      const y1 = (cy - h / 2 - offsetY) / scale;
      const x2 = (cx + w / 2 - offsetX) / scale;
      const y2 = (cy + h / 2 - offsetY) / scale;

      const bbox = {
        x: Math.max(0, x1),
        y: Math.max(0, y1),
        width: Math.min(srcWidth, x2) - Math.max(0, x1),
        height: Math.min(srcHeight, y2) - Math.max(0, y1)
      };

      const keypoints = [];
      for (let k = 0; k < numKeypoints; k++) {
        const kpBaseIdx = 5 + k * 3;
        const kpX = data[kpBaseIdx * numBoxes + i];
        const kpY = data[(kpBaseIdx + 1) * numBoxes + i];
        const kpConf = data[(kpBaseIdx + 2) * numBoxes + i];

        const originalX = (kpX - offsetX) / scale;
        const originalY = (kpY - offsetY) / scale;

        keypoints.push({
          x: Math.max(0, Math.min(srcWidth, originalX)),
          y: Math.max(0, Math.min(srcHeight, originalY)),
          score: kpConf,
          name: KEYPOINT_NAMES[k]
        });
      }

      detections.push({
        confidence: conf,
        bbox,
        keypoints
      });
    }

    const filteredDetections = this.nms(detections);

    this._frameCount++;
    if (this._frameCount % 30 === 1) {
      console.log(`[OnnxPose] Frame ${this._frameCount}: ${detections.length} raw, ${filteredDetections.length} after NMS`);
    }

    return filteredDetections.map(det => ({
      keypoints: det.keypoints,
      score: det.confidence,
      bbox: det.bbox
    }));
  }

  /**
   * Non-Maximum Suppression
   */
  nms(detections) {
    if (detections.length === 0) return [];

    detections.sort((a, b) => b.confidence - a.confidence);

    const kept = [];

    while (detections.length > 0) {
      const best = detections.shift();
      kept.push(best);

      detections = detections.filter(det => {
        return this.iou(best.bbox, det.bbox) < this.iouThreshold;
      });
    }

    return kept;
  }

  iou(box1, box2) {
    const x1 = Math.max(box1.x, box2.x);
    const y1 = Math.max(box1.y, box2.y);
    const x2 = Math.min(box1.x + box1.width, box2.x + box2.width);
    const y2 = Math.min(box1.y + box1.height, box2.y + box2.height);

    const intersection = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
    const area1 = box1.width * box1.height;
    const area2 = box2.width * box2.height;
    const union = area1 + area2 - intersection;

    return union > 0 ? intersection / union : 0;
  }

  getModelInfo() {
    return this.modelInfo;
  }

  async stop() {
    if (this.session) {
      await this.session.release();
      this.session = null;
    }
    this.isInitialized = false;
  }
}

export default OnnxYoloPoseDetector;
